import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type {
  OperatingHoursClosureDraft,
  OperatingHoursDateOverrideDraft,
  OperatingHoursDayDraft,
  OperatingHoursDraft,
  OperatingHoursScheduleDraft,
  OperatingHoursWeekDraft,
  OperatingHoursWindowDraft,
} from "./operating-hours";
import {
  newClosureDraft,
  newDateOverrideDraft,
  newScheduleDraft,
  OPERATING_HOURS_DAYS,
} from "./operating-hours";

const MAX_WINDOWS_PER_DAY = 4;

function newWindow(prefix: string, windows: readonly OperatingHoursWindowDraft[]) {
  let index = windows.length + 1;
  const ids = new Set(windows.map((window) => window.id));
  let id = `${prefix}-${index}`;
  while (ids.has(id)) {
    index += 1;
    id = `${prefix}-${index}`;
  }
  return { id, opensAt: "09:00", closesAt: "21:00" };
}

function WindowList({
  label,
  windows,
  onChange,
  disabled,
}: {
  label: string;
  windows: OperatingHoursWindowDraft[];
  onChange: (next: OperatingHoursWindowDraft[]) => void;
  disabled: boolean;
}) {
  return (
    <Stack spacing={1} sx={{ minWidth: 0 }}>
      {windows.map((window, index) => (
        <Box
          key={window.id}
          sx={{
            alignItems: "center",
            display: "grid",
            gap: 1,
            gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr) auto",
          }}
        >
          <TextField
            disabled={disabled}
            label={index === 0 ? "Opens" : undefined}
            onChange={(event) =>
              onChange(
                windows.map((current) =>
                  current.id === window.id
                    ? { ...current, opensAt: event.target.value }
                    : current,
                ),
              )
            }
            size="small"
            slotProps={{
              htmlInput: {
                "aria-label": `${label} window ${index + 1} opens`,
                inputMode: "numeric",
                pattern: "[0-2][0-9]:[0-5][0-9]",
              },
            }}
            value={window.opensAt}
          />
          <Typography color="text.secondary" variant="body2">
            to
          </Typography>
          <TextField
            disabled={disabled}
            helperText={index === 0 ? "Use 24:00 for midnight." : undefined}
            label={index === 0 ? "Closes" : undefined}
            onChange={(event) =>
              onChange(
                windows.map((current) =>
                  current.id === window.id
                    ? { ...current, closesAt: event.target.value }
                    : current,
                ),
              )
            }
            size="small"
            slotProps={{
              htmlInput: {
                "aria-label": `${label} window ${index + 1} closes`,
                inputMode: "numeric",
                pattern: "(?:[0-2][0-9]:[0-5][0-9]|24:00)",
              },
            }}
            value={window.closesAt}
          />
          <IconButton
            aria-label={`Remove ${label} window ${index + 1}`}
            color="error"
            disabled={disabled || windows.length === 1}
            onClick={() =>
              onChange(windows.filter((current) => current.id !== window.id))
            }
            size="small"
            type="button"
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Box>
        <Button
          aria-label={`Add ${label} window`}
          disabled={disabled || windows.length >= MAX_WINDOWS_PER_DAY}
          onClick={() => onChange([...windows, newWindow(label, windows)])}
          size="small"
          startIcon={<AddIcon />}
          type="button"
          variant="outlined"
        >
          Add window
        </Button>
      </Box>
    </Stack>
  );
}

function WeeklyScheduleEditor({
  value,
  onChange,
  disabled,
  label,
}: {
  value: OperatingHoursWeekDraft;
  onChange: (next: OperatingHoursWeekDraft) => void;
  disabled: boolean;
  label: string;
}) {
  function setDay(dayOfWeek: keyof OperatingHoursWeekDraft, day: OperatingHoursDayDraft) {
    onChange({ ...value, [dayOfWeek]: day });
  }

  return (
    <Stack divider={<Divider flexItem />}>
      {OPERATING_HOURS_DAYS.map(({ dayOfWeek, label: dayLabel }) => {
        const day = value[dayOfWeek];
        return (
          <Box
            key={dayOfWeek}
            role="group"
            aria-label={`${label} ${dayLabel}`}
            sx={{
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: { xs: "1fr", md: "9rem minmax(0, 1fr)" },
              py: 1.5,
            }}
          >
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Switch
                checked={day.enabled}
                disabled={disabled}
                onChange={(event) => {
                  const enabled = event.target.checked;
                  setDay(dayOfWeek, {
                    enabled,
                    windows:
                      enabled && day.windows.length === 0
                        ? [newWindow(`${label}-${dayOfWeek}`, [])]
                        : day.windows,
                  });
                }}
                size="small"
                slotProps={{ input: { "aria-label": `Open ${dayLabel}` } }}
              />
              <Typography sx={{ fontWeight: 700 }} variant="body2">
                {dayLabel}
              </Typography>
            </Stack>
            {day.enabled ? (
              <WindowList
                disabled={disabled}
                label={`${label} ${dayLabel}`}
                onChange={(windows) => setDay(dayOfWeek, { enabled: true, windows })}
                windows={day.windows}
              />
            ) : (
              <Typography color="text.secondary" sx={{ pt: 0.75 }} variant="body2">
                Closed
              </Typography>
            )}
          </Box>
        );
      })}
    </Stack>
  );
}

function ScheduleEditor({
  schedule,
  onChange,
  onRemove,
  disabled,
  removable,
}: {
  schedule: OperatingHoursScheduleDraft;
  onChange: (next: OperatingHoursScheduleDraft) => void;
  onRemove: () => void;
  disabled: boolean;
  removable: boolean;
}) {
  return (
    <Stack spacing={2} sx={{ py: 2 }}>
      <Box
        sx={{
          alignItems: { md: "center" },
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr) auto" },
        }}
      >
        <TextField
          disabled={disabled}
          label="Effective from"
          onChange={(event) => onChange({ ...schedule, effectiveFrom: event.target.value })}
          size="small"
          slotProps={{ htmlInput: { "aria-label": "Schedule effective from" } }}
          type="date"
          value={schedule.effectiveFrom}
        />
        <TextField
          disabled={disabled}
          helperText="Leave blank until this schedule is replaced."
          label="Ends before"
          onChange={(event) => onChange({ ...schedule, effectiveUntil: event.target.value })}
          size="small"
          slotProps={{ htmlInput: { "aria-label": "Schedule ends before" } }}
          type="date"
          value={schedule.effectiveUntil}
        />
        {removable ? (
          <Button
            color="error"
            disabled={disabled}
            onClick={onRemove}
            size="small"
            type="button"
          >
            Remove season
          </Button>
        ) : null}
      </Box>
      <WeeklyScheduleEditor
        disabled={disabled}
        label="Weekly schedule"
        onChange={(week) => onChange({ ...schedule, week })}
        value={schedule.week}
      />
    </Stack>
  );
}

function DateOverrideEditor({
  override,
  onChange,
  onRemove,
  disabled,
}: {
  override: OperatingHoursDateOverrideDraft;
  onChange: (next: OperatingHoursDateOverrideDraft) => void;
  onRemove: () => void;
  disabled: boolean;
}) {
  return (
    <Stack spacing={1.5} sx={{ py: 1.5 }}>
      <Box
        sx={{
          alignItems: { md: "center" },
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr) auto" },
        }}
      >
        <TextField
          disabled={disabled}
          label="Date"
          onChange={(event) => onChange({ ...override, localDate: event.target.value })}
          size="small"
          type="date"
          value={override.localDate}
        />
        <TextField
          disabled={disabled}
          label="Change"
          onChange={(event) =>
            onChange({
              ...override,
              kind: event.target.value as OperatingHoursDateOverrideDraft["kind"],
            })
          }
          select
          size="small"
          value={override.kind}
        >
          <MenuItem value="closed">Closed all day</MenuItem>
          <MenuItem value="replace">Replace hours</MenuItem>
        </TextField>
        <Button color="error" disabled={disabled} onClick={onRemove} size="small" type="button">
          Remove date
        </Button>
      </Box>
      {override.kind === "replace" ? (
        <WindowList
          disabled={disabled}
          label="Date override"
          onChange={(windows) => onChange({ ...override, windows })}
          windows={override.windows}
        />
      ) : null}
    </Stack>
  );
}

function ClosureEditor({
  closure,
  onChange,
  onRemove,
  disabled,
}: {
  closure: OperatingHoursClosureDraft;
  onChange: (next: OperatingHoursClosureDraft) => void;
  onRemove: () => void;
  disabled: boolean;
}) {
  return (
    <Box
      sx={{
        alignItems: { md: "center" },
        display: "grid",
        gap: 1.5,
        gridTemplateColumns: {
          xs: "1fr",
          md: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.5fr) auto",
        },
        py: 1.5,
      }}
    >
      <TextField
        disabled={disabled}
        label="Closed from"
        onChange={(event) => onChange({ ...closure, startsOn: event.target.value })}
        size="small"
        type="date"
        value={closure.startsOn}
      />
      <TextField
        disabled={disabled}
        label="Through"
        onChange={(event) => onChange({ ...closure, endsOn: event.target.value })}
        size="small"
        type="date"
        value={closure.endsOn}
      />
      <TextField
        disabled={disabled}
        label="Reason (optional)"
        onChange={(event) => onChange({ ...closure, reason: event.target.value })}
        size="small"
        value={closure.reason}
      />
      <Button color="error" disabled={disabled} onClick={onRemove} size="small" type="button">
        Remove closure
      </Button>
    </Box>
  );
}

export function OperatingHoursEditor({
  value,
  onChange,
  disabled = false,
}: {
  value: OperatingHoursDraft;
  onChange: (next: OperatingHoursDraft) => void;
  disabled?: boolean;
}) {
  function replaceSchedule(next: OperatingHoursScheduleDraft) {
    onChange({
      ...value,
      schedules: value.schedules.map((schedule) =>
        schedule.id === next.id ? next : schedule,
      ),
    });
  }

  return (
    <Stack spacing={3}>
      <Stack spacing={0.5}>
        <Typography component="h3" sx={{ fontWeight: 800 }} variant="subtitle1">
          Weekly schedules
        </Typography>
        <Typography color="text.secondary" variant="body2">
          Add a future season when regular hours change. A schedule stays in effect until its next season begins.
        </Typography>
      </Stack>
      <Stack divider={<Divider flexItem />}>
        {value.schedules.map((schedule) => (
          <ScheduleEditor
            disabled={disabled}
            key={schedule.id}
            onChange={replaceSchedule}
            onRemove={() =>
              onChange({
                ...value,
                schedules: value.schedules.filter((item) => item.id !== schedule.id),
              })
            }
            removable={value.schedules.length > 1}
            schedule={schedule}
          />
        ))}
      </Stack>
      <Box>
        <Button
          disabled={disabled || value.schedules.length >= 52}
          onClick={() => onChange({ ...value, schedules: [...value.schedules, newScheduleDraft()] })}
          size="small"
          startIcon={<AddIcon />}
          type="button"
          variant="outlined"
        >
          Add seasonal schedule
        </Button>
      </Box>

      <Divider />
      <Stack spacing={0.5}>
        <Typography component="h3" sx={{ fontWeight: 800 }} variant="subtitle1">
          Date exceptions
        </Typography>
        <Typography color="text.secondary" variant="body2">
          Close for a holiday or replace one date with special hours such as an early close.
        </Typography>
      </Stack>
      <Stack divider={<Divider flexItem />}>
        {value.dateOverrides.map((override) => (
          <DateOverrideEditor
            disabled={disabled}
            key={override.id}
            onChange={(next) =>
              onChange({
                ...value,
                dateOverrides: value.dateOverrides.map((item) =>
                  item.id === next.id ? next : item,
                ),
              })
            }
            onRemove={() =>
              onChange({
                ...value,
                dateOverrides: value.dateOverrides.filter((item) => item.id !== override.id),
              })
            }
            override={override}
          />
        ))}
      </Stack>
      <Box>
        <Button
          disabled={disabled || value.dateOverrides.length >= 366}
          onClick={() => onChange({ ...value, dateOverrides: [...value.dateOverrides, newDateOverrideDraft()] })}
          size="small"
          startIcon={<AddIcon />}
          type="button"
          variant="outlined"
        >
          Add date exception
        </Button>
      </Box>

      <Divider />
      <Stack spacing={0.5}>
        <Typography component="h3" sx={{ fontWeight: 800 }} variant="subtitle1">
          Closure periods
        </Typography>
        <Typography color="text.secondary" variant="body2">
          Use a date range for maintenance, a holiday break, or another full closure.
        </Typography>
      </Stack>
      <Stack divider={<Divider flexItem />}>
        {value.closurePeriods.map((closure) => (
          <ClosureEditor
            closure={closure}
            disabled={disabled}
            key={closure.id}
            onChange={(next) =>
              onChange({
                ...value,
                closurePeriods: value.closurePeriods.map((item) =>
                  item.id === next.id ? next : item,
                ),
              })
            }
            onRemove={() =>
              onChange({
                ...value,
                closurePeriods: value.closurePeriods.filter((item) => item.id !== closure.id),
              })
            }
          />
        ))}
      </Stack>
      <Box>
        <Button
          disabled={disabled || value.closurePeriods.length >= 24}
          onClick={() => onChange({ ...value, closurePeriods: [...value.closurePeriods, newClosureDraft()] })}
          size="small"
          startIcon={<AddIcon />}
          type="button"
          variant="outlined"
        >
          Add closure period
        </Button>
      </Box>
    </Stack>
  );
}
